import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ref, getDownloadURL } from "firebase/storage";
import { storage } from '../firebase';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import AuthFeaturesSection from '../components/AuthFeaturesSection';
import AuthCard from '../components/AuthCard';

const AuthPage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  
  const [bgImageUrl, setBgImageUrl] = useState<string | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);

  // DEV/UAT BYPASS check for email verification status polling
  const isDevOrUat = (import.meta as any).env.MODE === 'development' || (import.meta as any).env.MODE === 'uat';
  const emailLower = currentUser?.email?.toLowerCase() || '';
  const isTestUser = emailLower.includes('test') || emailLower.endsWith('@example.com');
  const showVerificationUI = (verificationSent || (currentUser && !currentUser.emailVerified)) && !(isDevOrUat && isTestUser);

  useEffect(() => {
    const fetchBackgroundImage = async () => {
        try {
            const backgroundRef = ref(storage, 'assets/login-background.jpg');
            const url = await getDownloadURL(backgroundRef);
            setBgImageUrl(url);
        } catch (err) {
            console.warn("Could not load background image from Firebase Storage. Using gradient fallback.", err);
            setBgImageUrl(null);
        }
    };
    fetchBackgroundImage();
  }, []);

  // Polling for verification status
  useEffect(() => {
      let interval: any;
      if (showVerificationUI && currentUser && !currentUser.emailVerified) {
          interval = setInterval(async () => {
              try {
                  await currentUser.reload();
                  if (currentUser.emailVerified) {
                      navigate(returnTo || '/', { replace: true }); 
                  }
              } catch (e) {
                  // Ignore errors during polling
              }
          }, 3000);
      }
      return () => {
          if (interval) clearInterval(interval);
      };
  }, [showVerificationUI, currentUser, navigate]);

  return (
    <div 
      className="h-full min-h-screen py-12 flex items-center justify-center p-4 relative bg-gray-900 transition-all duration-1000 ease-in-out"
      style={{
        backgroundImage: bgImageUrl ? `url('${bgImageUrl}')` : 'linear-gradient(135deg, #111827 0%, #0e7490 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <SEO title="Sign In | RaceLineIQ" />
      <div className="absolute inset-0 bg-gray-900/80 backdrop-blur-sm"></div>

      <div className="w-full max-w-6xl relative z-10 flex flex-col lg:flex-row gap-8 lg:gap-16 items-center">
        {/* Left Column: Educational Content (Memoized component) */}
        <AuthFeaturesSection />

        {/* Right Column: Auth Card (Isolated form & input state) */}
        <AuthCard 
          verificationSent={verificationSent}
          setVerificationSent={setVerificationSent}
        />
      </div>
    </div>
  );
};

export default AuthPage;
